import Cta, { CtaStyle, CtaType } from './index';
import calculateRate from '../util/calculate-rate';
import getCtaType from '../util/cta-type';
import configuration from '../configuration';
import commonCss from './css/common.css';
import { createLinkTag, createStyleTag, CssVariables, StyleTag } from '../util/create-style-tag';
import AssetWrapper from '../asset/wrappers';
import { StartPrequal } from '../events';
import { lowestNumber } from '../util/parse-data';
import { infoSvgComponent, bannerSvgComponent, calculatorSvgComponent } from '../util/svg-component';
import OlToolTip from './tooltip';
import Asset from '../asset/asset';

export default abstract class GenericCta<AssetType extends Asset> extends Cta<AssetType> {
  public showMonthlyPayment = configuration.show_monthly_payment;
  public styleTag?: StyleTag = undefined;
  tooltip?: OlToolTip<AssetType>;

  constructor(
    wrapper: AssetWrapper<AssetType>,
    options?: {
      showMonthlyPayment?: boolean;
      style?: CtaStyle;
      styleTag?: StyleTag;
    }
  ) {
    super(wrapper);
    this.style = options?.style !== undefined ? options.style : this.style;
    if (
      configuration.flow_type === 'lead_first' &&
      configuration.enable_estimate_payment_step &&
      (this.style === 'standard' || this.style === 'slim')
    ) {
      this.style = 'estimate-payment';
    }
    if (options?.showMonthlyPayment !== undefined) {
      this.showMonthlyPayment = this.showMonthlyPayment && options.showMonthlyPayment;
    }

    // option created to allow style the CTA button
    this.styleTag = options?.styleTag;

    // Show the Prequal modal when the CTA is clicked
    this.onClick((event: Event) => {
      const target = event.target as HTMLElement;
      const clickedButton = target?.closest('.ol-button');
      const buttonType = clickedButton?.getAttribute('data-button-type');

      if (buttonType === 'estimate-payment') {
        configuration.show_estimate_payment_step = true;
      } else {
        configuration.show_estimate_payment_step = false;
      }

      if (['banner', 'sidebar'].includes(this.style)) {
        configuration.enable_vehicle_selection_step = true;
      } else {
        configuration.enable_vehicle_selection_step = false;
      }

      const ctaType: CtaType = getCtaType(this.wrapper.platform.pageType, this.style);
      configuration.cta_type = ctaType;

      wrapper.platform.eventBus.dispatchEvent(
        new StartPrequal({
          detail: {
            asset: wrapper.asset,
            cta_type: ctaType
          }
        })
      );
    });
  }

  returnCssVariables(): CssVariables {
    return {
      buttonBackgroundColor: configuration.cta_button_color,
      buttonTextColor: configuration.cta_button_text_color,
      miniCtaButtonMsrpTextColor: configuration.minicta_button_msrp_text_color,
      tooltipBackgroundColor: '#ffffff',
      tooltipTextColor: '#666666',
      mobileBreakpoint: '992px'
    };
  }

  returnCtaStylesTags(): StyleTag[] {
    const cssTags = [createStyleTag(commonCss.toString(), 'cta-base-style')];
    if (this.styleTag) cssTags.push(this.styleTag);
    return cssTags;
  }

  returnDocumentStyleTags(): StyleTag[] {
    return [createLinkTag('https://fonts.googleapis.com/css?family=Karla:400,700|Nunito:400,700')];
  }

  buildGenericCtaElement(asset: AssetType, rate: number): HTMLElement {
    // CTA Container
    const cta = document.createElement('a');
    // Add generic CTA classes
    cta.classList.add('ol-cta', 'ol-cta-search');
    if (this.style === 'slim') cta.classList.add('ol-cta-slim');
    else if (this.style === 'standard') cta.classList.add('ol-cta-standard');
    else throw new Error(`CTA type "${this.style}" not expected.`);

    // Prepare buttonContainer
    const buttonContainer = document.createElement('div');
    buttonContainer.addEventListener('click', this.handleOnClick.bind(this));
    buttonContainer.classList.add('ol-button-container-search');
    buttonContainer.innerHTML = `
    <div class="ol-button ol-button-search" id="ol-cta-button" data-links-initialized="false">
      <span class="ol-title ol-button-text-search">${configuration.cta_text}</span>
    </div>
    `;

    // N2O-1726 we need to pass along the price attributes even if we don't want to show it on the page
    const priceAttributes =
      asset.price && asset.price > 0 ? `originalPrice="${asset.originalPrice}" price="${asset.price}"` : '';

    cta.innerHTML = `
    <div class="ol-msrp-container ol-msrp-container-search" ${priceAttributes}>
      <div class="ol-rate-container-search">
        <p class="ol-body">from</p>
        <div class="ol-tooltip-link">
          <p class="ol-title ol-msrp-search">$${rate}</p>
          <p class="ol-body ol-month-label">/ mo.</p>
          ${infoSvgComponent().outerHTML}
        </div>
      </div>
      <p class="ol-body ol-msrp-subtitle-search">Instant • No credit impact</p>
    </div>
    `;
    // Standard No Payment button is before info text
    if (!(this.showMonthlyPayment && rate > 0) && this.style === 'standard') cta.prepend(buttonContainer);
    // Otherwise, regular button placement is after info text
    else cta.append(buttonContainer);
    return cta;
  }

  buildBannerCtaElement(): HTMLElement {
    // CTA Container
    const cta = document.createElement('a');
    // Add generic CTA classes
    cta.classList.add('ol-cta-search', 'ol-cta-banner');
    const ctaColor =
      this.wrapper.platform.pageType === 'homepage'
        ? configuration.homepage_cta_button_color
        : configuration.srp_cta_button_color;
    cta.style.borderColor = ctaColor;

    const bannerContainer = document.createElement('div');
    bannerContainer.classList.add('ol-container-banner');

    // Prepare buttonContainer
    const buttonContainer = document.createElement('div');
    buttonContainer.addEventListener('click', this.handleOnClick.bind(this));
    buttonContainer.classList.add('ol-button-container-banner');
    buttonContainer.innerHTML = `
    <div class="ol-button ol-button-banner" id="ol-cta-button" data-links-initialized="false">
      <span class="ol-banner-button-title">Get Started</span>
    </div>
    `;

    bannerContainer.innerHTML = `
    <div class="ol-banner-icon">
        ${
          bannerSvgComponent({
            banner_cta_button_color: ctaColor
          }).outerHTML
        }
    </div>
    <div class="ol-banner-text">
      <div>
          <p class="ol-banner-title">What can I afford?</p>
      </div>
      <div>
          <p class="ol-banner-body">Build your deal and understand your spending power</p>
      </div>
    </div>`;
    cta.append(bannerContainer);
    cta.append(buttonContainer);
    return cta;
  }

  buildEstimatePaymentCtaElement(asset: AssetType, rate: number): HTMLElement {
    // CTA Container
    const cta = document.createElement('a');
    // Add generic CTA classes
    cta.classList.add('ol-cta', 'ol-cta-search', 'has-estimate-payment');

    // Prepare buttonContainer
    const buttonContainer = document.createElement('div');
    buttonContainer.addEventListener('click', this.handleOnClick.bind(this));
    buttonContainer.classList.add('ol-button-container-search');
    buttonContainer.innerHTML = `
    <div class="ol-button ol-button-search-estimate-payment" style="margin-bottom:8px" id="ol-cta-button" data-links-initialized="false" data-button-type="get-prequalified">
      <span class="ol-title ol-button-text-search">${configuration.cta_text}</span>
    </div>
    <div class="ol-button ol-button-search-estimate-payment" id="ol-cta-button" data-links-initialized="false" data-button-type="estimate-payment">
      <span class="ol-title ol-button-text-search">${configuration.estimate_payment_cta_text}</span>
    </div>
    `;

    // N2O-1726 we need to pass along the price attributes even if we don't want to show it on the page
    const priceAttributes =
      asset.price && asset.price > 0 ? `originalPrice="${asset.originalPrice}" price="${asset.price}"` : '';

    cta.innerHTML = `
    <div class="ol-msrp-container ol-msrp-container-search" ${priceAttributes}>
      <div class="ol-rate-container-search">
        <p class="ol-body">from</p>
        <div class="ol-tooltip-link">
          <p class="ol-title ol-msrp-search">$${rate}</p>
          <p class="ol-body ol-month-label">/ mo.</p>
          ${infoSvgComponent().outerHTML}
        </div>
      </div>
      <p class="ol-body ol-msrp-subtitle-search">Instant • No credit impact</p>
    </div>
    `;
    cta.append(buttonContainer);
    return cta;
  }

  buildMiniCtaElement(asset: AssetType, rate: number): HTMLElement {
    // CTA Container
    const cta = document.createElement('a');
    cta.classList.add('ol-cta', 'ol-cta-search', 'ol-cta-mini', 'ol-button');
    cta.innerHTML = `
      <div class="ol-button-text">
        <span>${configuration.cta_text}</span>
      </div>
      <div class="ol-msrp-container">
        <span class="ol-cta-text-msrp">as low as $${rate}<span>/mo</span></span>
        <span class="ol-cta-tooltip-button ol-tooltip-link">
            ${
              infoSvgComponent({
                infoButtonColor: configuration.minicta_button_msrp_text_color
              }).outerHTML
            }
        </span>
      </div>
    `;
    cta.addEventListener('click', (ev: MouseEvent) => {
      // This code was required to allow the info icon to be clicked on mobile version
      // without displaying the application form modal.
      const firedFromInfoButton = !!ev
        .composedPath()
        .find((e) => e instanceof HTMLElement && e.classList.contains('ol-cta-tooltip-button'));
      if (firedFromInfoButton) return;
      this.handleOnClick.call(this, ev);
    });
    return cta;
  }

  buildSidebarCtaElement(): HTMLElement {
    const cta = document.createElement('div');
    cta.classList.add('ol-cta-sidebar');

    const closeButton = document.createElement('button');
    closeButton.classList.add('sidebar-close');
    closeButton.innerHTML = '&times;';
    closeButton.addEventListener('click', () => {
      cta.classList.add('collapsed');
    });

    cta.appendChild(closeButton);

    const iconAndLabel = document.createElement('div');
    iconAndLabel.classList.add('sidebar-header');

    const icon = calculatorSvgComponent();
    icon.classList.add('sidebar-icon');

    const label = document.createElement('span');
    label.textContent = 'Build My Payment';
    label.classList.add('sidebar-label');

    iconAndLabel.appendChild(icon);
    iconAndLabel.appendChild(label);

    const button = document.createElement('button');
    button.textContent = 'Get Started';
    button.classList.add('sidebar-button');
    button.addEventListener('click', this.handleOnClick.bind(this));

    cta.appendChild(iconAndLabel);
    cta.appendChild(button);

    icon.addEventListener('click', () => {
      if (cta.classList.contains('collapsed')) {
        cta.classList.remove('collapsed');
      }
    });
    return cta;
  }

  buildCtaElement(asset: AssetType, rate: number): HTMLElement {
    if (this.style === 'standard' || this.style === 'slim') {
      return this.buildGenericCtaElement(asset, rate);
    } else if (this.style === 'mini') {
      return this.buildMiniCtaElement(asset, rate);
    } else if (this.style === 'estimate-payment') {
      return this.buildEstimatePaymentCtaElement(asset, rate);
    } else if (this.style === 'banner') {
      return this.buildBannerCtaElement();
    } else if (this.style === 'sidebar') {
      return this.buildSidebarCtaElement();
    } else throw new Error(`CTA type "${this.style}" not expected.`);
  }

  generateHTML() {
    const asset: AssetType = this.wrapper.asset;
    // low price may be zero, but it lets us send a number instead of null to all our functions like calcRate
    const lowestPrice: number = lowestNumber(...asset.prices);
    const showMonthlyPayment = this.showMonthlyPayment && lowestPrice > 0;

    const rate = calculateRate(
      lowestPrice,
      configuration.doc_fee,
      configuration.apr_percent,
      configuration.term_months
    );

    const cta = this.buildCtaElement(asset, rate);

    if (!showMonthlyPayment) {
      cta.classList.add('ol-no-payment');
    }

    // We only check if the tooltip exists if a ol-tooltip-* is added to the cta style
    const tooltipDiv = cta.querySelector<HTMLElement>('[class^="ol-tooltip"]');
    if (tooltipDiv) {
      const tooltipElement = cta.querySelector<HTMLElement>('.ol-info-button');
      if (!tooltipElement) throw new Error('The info button element was not found on the CTA');
      this.tooltip = new OlToolTip<AssetType>(this.wrapper.asset, this, tooltipElement, lowestPrice);
    }
    return cta;
  }
}
